import { useEffect } from "react";
import { Keyboard, X } from "lucide-react";

interface Shortcut { keys: string[]; description: string }

const SHORTCUTS: Shortcut[] = [
  { keys: ["/"], description: "Focus the search box" },
  { keys: ["n"], description: "Catalog a new item" },
  { keys: ["g"], description: "Toggle grid / list view" },
  { keys: ["f"], description: "Open filters (mobile)" },
  { keys: ["?"], description: "Show this shortcuts panel" },
  { keys: ["Esc"], description: "Close panels, clear selection, dismiss" },
];

export function HelpOverlay({ open, onClose }: { open: boolean; onClose: () => void }) {
  // Esc handling — `useKeyboardShortcuts` only owns its host page, so we wire
  // a local listener here.
  useEffect(() => {
    if (!open) return;
    function onKey(e: KeyboardEvent) {
      if (e.key === "Escape") onClose();
    }
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  if (!open) return null;
  return (
    <div
      data-testid="help-overlay"
      role="dialog"
      aria-modal="true"
      aria-labelledby="help-overlay-title"
      className="fixed inset-0 z-50 grid place-items-center bg-charcoal/45 backdrop-blur-sm px-4"
      onClick={onClose}
    >
      <div
        className="bg-background border border-border max-w-md w-full p-6 sm:p-8 relative"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          type="button"
          onClick={onClose}
          aria-label="Close help"
          data-testid="help-overlay-close"
          className="absolute top-4 right-4 text-muted-foreground hover:text-oxblood"
        >
          <X className="w-4 h-4" />
        </button>

        <p className="text-[11px] uppercase tracking-[0.28em] text-oxblood font-medium flex items-center gap-2">
          <Keyboard className="w-3.5 h-3.5" /> Shortcuts
        </p>
        <h2 id="help-overlay-title" className="font-display text-2xl mt-2">
          Move faster with the keyboard.
        </h2>

        <ul className="mt-6 space-y-2.5" data-testid="help-overlay-list">
          {SHORTCUTS.map((sc) => (
            <li key={sc.keys.join("+")} className="flex items-center justify-between gap-4 text-sm">
              <span className="text-foreground/80">{sc.description}</span>
              <span className="flex gap-1 shrink-0">
                {sc.keys.map((k) => (
                  <kbd
                    key={k}
                    className="font-mono text-[11px] uppercase tracking-wider px-2 py-0.5 border border-border bg-bone/40"
                  >
                    {k}
                  </kbd>
                ))}
              </span>
            </li>
          ))}
        </ul>

        <p className="mt-6 text-[11px] text-muted-foreground">
          Tip — shortcuts are ignored while you're typing in a text field.
        </p>
      </div>
    </div>
  );
}
